import { Component, type CSSProperties, type ErrorInfo, type ReactNode } from 'react';
import { STORAGE_KEY as LIVE_SESSION_KEY } from '@/state/LiveSessionContext';
import i18n from '@/i18n/config';

// Top-level error boundary. A single render throw anywhere in the tree would
// otherwise unmount everything and leave a black screen (no theming, no UI) —
// see the live-session crash that motivated this. Catching it here gives the
// user a themed, recoverable fallback instead.
//
// The "Discard stuck workout" escape hatch exists because a corrupt/incompatible
// persisted live session (localStorage key optimize.liveSession.v1) can crash the
// session route on every load; clearing it lets the user back into the app
// without devtools. Already-saved sets stay on the server-side workout.

// Production hides the raw error; every other environment (localhost dev, the
// dev container app, *.azurecontainerapps.io preview FQDNs) shows it to speed up
// triage. track.optimizehealth.ca is the only production host, so a simple host
// comparison distinguishes prod from everything else.
const PROD_HOST = 'track.optimizehealth.ca';

function shouldShowDetails(): boolean {
  return typeof window !== 'undefined' && window.location.hostname !== PROD_HOST;
}

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surfaces in the console for remote debugging (Safari Web Inspector, logs).
    console.error('[ErrorBoundary] caught render error:', error, info.componentStack);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  private handleDiscardWorkout = (): void => {
    try {
      localStorage.removeItem(LIVE_SESSION_KEY);
    } catch {
      /* private mode / quota — nothing else we can do */
    }
    // Full document load (not client nav) so we start from a clean React tree.
    window.location.assign('/train');
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div role="alert" style={styles.scrim}>
        <div style={styles.card}>
          <div style={styles.title}>{i18n.t('components.errorTitle')}</div>
          <p style={styles.body}>
            {i18n.t('components.errorBody')}
          </p>
          <div style={styles.actions}>
            <button type="button" style={styles.primaryBtn} onClick={this.handleReload}>
              {i18n.t('components.errorReload')}
            </button>
            <button type="button" style={styles.secondaryBtn} onClick={this.handleDiscardWorkout}>
              {i18n.t('components.errorDiscardWorkout')}
            </button>
          </div>
          {shouldShowDetails() && (
            <pre style={styles.details}>
              {error.message}
              {error.stack ? `\n\n${error.stack}` : ''}
            </pre>
          )}
        </div>
      </div>
    );
  }
}

// Inline styles (not a CSS file) so the fallback renders even if a stylesheet
// failed to load. Theme custom properties are used when present, with literal
// light-theme fallbacks so the screen is never black/unreadable.
const styles: Record<string, CSSProperties> = {
  scrim: {
    minHeight: '100dvh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    background: 'var(--bg, #f4f2ee)',
    color: 'var(--ink, #1a1714)',
    fontFamily: 'var(--font-stack, -apple-system, BlinkMacSystemFont, system-ui, sans-serif)',
    boxSizing: 'border-box',
  },
  card: {
    width: '100%',
    maxWidth: 420,
    background: 'var(--surface, #ffffff)',
    border: '1px solid var(--hairline, rgba(26, 23, 20, 0.08))',
    borderRadius: 16,
    padding: '24px',
    boxShadow: 'var(--shadow-card, 0 6px 22px rgba(0, 0, 0, 0.08))',
  },
  title: {
    fontSize: 18,
    fontWeight: 650,
    marginBottom: 8,
  },
  body: {
    fontSize: 14,
    lineHeight: 1.5,
    color: 'var(--ink-dim, rgba(26, 23, 20, 0.62))',
    margin: '0 0 20px',
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
  },
  primaryBtn: {
    flex: '1 1 auto',
    minHeight: 44,
    padding: '0 16px',
    borderRadius: 12,
    border: 'none',
    cursor: 'pointer',
    fontSize: 15,
    fontWeight: 600,
    background: 'var(--accent, #2f6f76)',
    color: 'var(--accent-contrast, #ffffff)',
  },
  secondaryBtn: {
    flex: '1 1 auto',
    minHeight: 44,
    padding: '0 16px',
    borderRadius: 12,
    cursor: 'pointer',
    fontSize: 15,
    fontWeight: 600,
    background: 'transparent',
    color: 'var(--ink, #1a1714)',
    border: '1px solid var(--hairline, rgba(26, 23, 20, 0.18))',
  },
  details: {
    marginTop: 20,
    padding: 12,
    borderRadius: 10,
    background: 'var(--surface-alt, #faf8f4)',
    color: 'var(--ink-dim, rgba(26, 23, 20, 0.62))',
    fontSize: 12,
    lineHeight: 1.45,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowX: 'auto',
    maxHeight: 240,
  },
};
